import { CheckCircle2, Ban } from "lucide-react";
import { useSeo, useSite } from "../lib/site";
import { SERVICE_CATS, PageHero, FinalCta, QuoteButton, CallButton } from "../components/site/Blocks";
import { AREA_LINE } from "../lib/cities";

function Category({ cat, items, children }) {
  const Icon = cat.icon;
  return (
    <section id={cat.key} className="scroll-mt-32 border-b border-neutral-200 py-12" data-testid={`services-section-${cat.key}`}>
      <div className="grid gap-6 md:grid-cols-[0.9fr_1.1fr]">
        <div>
          <Icon className="h-10 w-10 text-[#D9232E]" aria-hidden="true" />
          <h2 className="mt-3 text-3xl font-bold uppercase sm:text-4xl">{cat.title}</h2>
          <p className="mt-2 text-lg text-neutral-700">{cat.blurb}</p>
        </div>
        <div>
          {items.length > 0 ? (
            <ul className="grid gap-3">
              {items.map((s) => (
                <li key={s.key} className="flex gap-3 rounded-md bg-[#F5F5F5] p-4" data-testid={`service-item-${s.key}`}>
                  <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-[#D9232E]" aria-hidden="true" />
                  <span><strong className="block text-lg">{s.name}</strong><span className="text-base text-neutral-700">{s.description}</span></span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-md bg-[#F5F5F5] p-5 text-base" data-testid={`services-empty-${cat.key}`}>Specific tasks in this category are being confirmed. Call us or send a request describing what you need, and we'll let you know if it's something we can help with.</p>
          )}
          {children}
        </div>
      </div>
    </section>
  );
}

export default function Services() {
  useSeo("Services", "Handyman help, junk removal, yard cleanup and property and rental cleanouts in Albuquerque, Rio Rancho, Los Lunas, Corrales and Santa Fe, NM. Clear written estimates before any work begins.");
  const { site } = useSite();
  const list = site.services || [];
  return (
    <div data-testid="services-page">
      <PageHero eyebrow="Services" title="Handyman, hauling & cleanup services">
        <span className="block font-bold" data-testid="services-area-line">{AREA_LINE}</span>
        Every job starts with a clear written estimate. Nothing is scheduled or charged until you approve it.
      </PageHero>
      <div className="wrap">
        {SERVICE_CATS.map((c) => (
          <Category key={c.key} cat={c} items={list.filter((s) => s.category === c.key)}>
            {c.key === "junk" && <p className="mt-4 text-base text-neutral-700">Appliances must already be disconnected. We don't haul hazardous materials (chemicals, paint, fuel, asbestos, medical waste). Donation drop-offs depend on what local charities accept.</p>}
            {c.key === "senior" && <a href="/senior-home-support" className="btn-outline mt-4" data-testid="services-senior-link">Senior Home Support details</a>}
          </Category>
        ))}
        <section className="py-12" data-testid="services-restricted">
          <div className="flex gap-4 rounded-lg border-2 border-neutral-300 p-6">
            <Ban className="h-8 w-8 shrink-0 text-neutral-600" aria-hidden="true" />
            <div>
              <h2 className="text-2xl font-bold uppercase">Work we don't currently offer</h2>
              <p className="mt-2 text-base text-neutral-700">For your safety and to follow New Mexico licensing rules, we don't perform regulated trade work: {(site.restricted || ["Electrical work", "Plumbing work", "Gas work", "HVAC work", "Structural repairs", "Demolition"]).join(", ").toLowerCase()}. Please contact an appropriately licensed contractor for these.</p>
            </div>
          </div>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <QuoteButton testid="services-quote-btn" />
            <CallButton className="btn-outline" testid="services-call-btn" />
          </div>
        </section>
      </div>
      <FinalCta />
    </div>
  );
}
